import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Site } from '../_models/site';
import { User } from '../_models/user';
import { AuthenticationService } from './authentication.service';
import { OrdinalPipe } from '../application-pipes/ordinal.pipe';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  currentUser: User | null;
  currentTenantId = '';
  public sites$: BehaviorSubject<any[]> = new BehaviorSubject(undefined);
  public assetTypes$: BehaviorSubject<any[]> = new BehaviorSubject(undefined);
  public settings$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  public tenants$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  public assets$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  public gateways$: BehaviorSubject<any> = new BehaviorSubject(undefined);

  constructor(
    private httpClient: HttpClient,
    private authenticationService: AuthenticationService,
    private ordinalPipe: OrdinalPipe
  ) {
    this.authenticationService.currentAccountValue.subscribe(x => {
      this.currentUser = x;
      this.currentTenantId = x?.tenantId;
    });
  }

  public getDashboard() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/dashboard`
    );
  }

  public getGlobalDashboard() {
    return this.httpClient.get(
      `${environment.apiPrefix}${
        this.currentTenantId
      }${environment.apiSuffix.replace('/api', '')}/api/dashboard`
    );
  }

  public getRoles() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/roles`
    );
  }

  public getSiteDetail(siteId) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/sites/${siteId}`
    );
  }

  public getDeviceDetails(deviceIds: string[], start, end) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/data/detailed`,
      { deviceIds, start, end }
    );
  }

  public getDeviceModels() {
    let obs = this.httpClient.get<any>(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices/models`
    );
    obs.subscribe((res: any) => {
      if (res) this.tenants$.next(res);
    });
    return obs;
  }

  public getAssets() {
    return this.assets$.asObservable();
  }

  public getGateways() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateways`
    );
  }

  public createGateway(gateway: any) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateways`,
      gateway
    );
  }

  public updateGateway(gateway: any) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateways/${gateway._id}`,
      gateway
    );
  }

  public checkGatewayInLNS(gatewayId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateways/${gatewayId}/check-lns`
    );
  }

  public deleteGateway(gateway, force: boolean = false) {
    const url = `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateways/${gateway._id}`;
    if (force) {
      return this.httpClient.delete(url, { params: { force: 'true' } });
    }
    return this.httpClient.delete(url);
  }

  public getGatewayModels() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/gateway-models`
    );
  }

  public getAssetTypes() {
    let obs = this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/types`
    );

    obs.subscribe((res: any) => {
      if (res) this.assetTypes$.next(res);
    });

    return obs;
  }

  addAsset(asset: any) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/provision`,
      asset
    );
  }

  public getAssetDetails(assetId) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/${assetId}`
    );
  }

  public downloadFile(fileId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/files/${fileId}/download`,
      {
        responseType: 'blob',
      }
    );
  }

  public assetReport(assetId, start, end) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/${assetId}/report`,
      { start, end }
    );
  }

  public updateAsset(asset: any) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/${asset._id}`,
      asset
    );
  }

  public deleteAsset(asset) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/${asset._id}`
    );
  }

  public deleteAssetAndDevice(asset) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/unprovision`,
      asset
    );
  }

  public getDevices() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices`
    );
  }

  public replaceDevice(device: any) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/assets/replace-device`,
      device
    );
  }

  public getDeviceById(deviceId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices/${deviceId}`
    );
  }

  public createDevice(device) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices`,
      device
    );
  }

  public updateDevice(device) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices/${device._id}`,
      device
    );
  }

  public deleteDevice(device) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/devices/${device._id}`
    );
  }

  public getUsers() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/users`
    );
  }

  public getUserById(userId) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/users/${userId}`
    );
  }

  public createUser(user) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/users/invite`,
      user
    );
  }

  public updateUser(user) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/users/${user._id}`,
      user
    );
  }

  public deleteUser(user) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/users/${user._id}`
    );
  }

  public getRules() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules`
    );
  }

  public getRuleById(ruleId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules/${ruleId}`
    );
  }

  public getRulesByAssetId(assetId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules?asset=${assetId}`
    );
  }

  public createRule(rule) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules`,
      rule
    );
  }

  public updateRule(rule) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules/${rule._id}`,
      rule
    );
  }

  public deleteRule(rule) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules/${rule._id}`
    );
  }

  public createSchedule(schedule) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/schedules`,
      schedule
    );
  }

  public updateSchedule(schedule) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/schedules/${schedule._id}`,
      schedule
    );
  }

  public getScheduleById(id: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/schedules/${id}`
    );
  }

  public deleteSchedule(schedule) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/schedules/${schedule._id}`
    );
  }

  public getInvocationsByRuleId(
    ruleId: string,
    startDate: string,
    endDate: string,
    limit = 10
  ) {
    const params = new HttpParams()
      .append('startDate', startDate)
      .append('endDate', endDate)
      .append('limit', limit);
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/rules/${ruleId}/invocations`,
      { params }
    );
  }

  public updateSettings(settings) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/settings`,
      settings
    );
  }

  public getSites() {
    let sitesObservable = this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/sites`
    );
    sitesObservable.subscribe((res: any[]) => {
      if (res) this.sites$.next(res);
    });
    return sitesObservable;
  }

  public createSite(site) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/sites`,
      site
    );
  }

  public updateSite(siteId, data) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/sites/${siteId}/buildings`,
      data
    );
  }

  public deleteSite(site) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/sites/${site._id}`
    );
  }

  public getSettings() {
    const sub = this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/settings`
    );
    sub.subscribe(res => {
      this.settings$.next(res);
    });
    return sub;
  }

  public getHealth() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/health`
    );
  }

  /* public getTenants() {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants`
    );
  } */

  public getTenants() {
    let obs = this.httpClient.get<any>(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants`
    );
    obs.subscribe((res: any) => {
      if (res) this.tenants$.next(res);
    });
    return obs;
  }

  public getTenantById(id: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants/${id}`
    );
  }

  public getTenantLimit() {
    return this.httpClient.get<{ limitReached: boolean }>(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants-limit`
    );
  }

  public createTenant(tenant: any) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants`,
      tenant
    );
  }

  public updateTenant(tenant: any) {
    return this.httpClient.patch(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants/${tenant._id}`,
      tenant
    );
  }

  public retryTenantStep(tenantId: string, step: string) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants/retry-step`,
      { tenantId, step }
    );
  }

  public markStepDone(tenantId: string, step: string, cname?: string) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/tenants/mark-step-done`,
      { tenantId, step, cname }
    );
  }

  public getIntegrations(type?: string) {
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/integrations`,
      {
        params,
      }
    );
  }

  public getIntegration(integrationId: string) {
    return this.httpClient.get(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/integrations/${integrationId}`
    );
  }

  public createIntegration(integrationData: any) {
    return this.httpClient.post(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/integrations`,
      integrationData
    );
  }

  public updateIntegration(integrationId: string, integrationData: any) {
    return this.httpClient.put(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/integrations/${integrationId}`,
      integrationData
    );
  }

  public deleteIntegration(integrationId: string) {
    return this.httpClient.delete(
      `${environment.apiPrefix}${this.currentTenantId}${environment.apiSuffix}/integrations/${integrationId}`
    );
  }
}
